import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { format, isAfter, isValid, parseISO } from "date-fns";
import { isoFormat } from "../../../../../common/helpers/formatDateTime";
import { SingleEditFormRow } from "../SingleEditFormRow";
import { useContext, useEffect, useMemo } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { changePeriodStartDate } from "../../../../employment/employmentSlice";
import { AccessRightContext } from "../../../../user/AccessRightProvider";
import FeatureFlag from "../../../../../common/components/functional/FeatureFlag";
import { FeatureFlagName } from "../../../../../app/featureFlags/featureFlags";
import { PeriodStartSettings } from "../periodStartSettings/PeriodStartSettings";

export const PeriodStarts = () => {
  const employment = useAppSelector((state) => state.employment.additionalInfo);
  const hasTimelineContract =
    employment.details?.periodStartBaseline !== undefined &&
    employment.details?.periodStartSettings !== undefined;
  return (
    <FeatureFlag
      name={FeatureFlagName.EMPLOYMENT_PERIOD_START_SETTINGS}
      fallback={<LegacyPeriodStarts />}
    >
      {!employment.isShift &&
      hasTimelineContract &&
      employment.details.periodStartBaseline !== null ? (
        <PeriodStartSettings
          key={employment.employmentId}
          employment={employment}
        />
      ) : (
        <LegacyPeriodStarts />
      )}
    </FeatureFlag>
  );
};

const LegacyPeriodStarts = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("talentProfile");

  const employment = useAppSelector((state) => state.employment.additionalInfo);

  const { canEditEmployment } = useContext(AccessRightContext);
  const hasHistory = (employment.details?.periodStartSettings?.length ?? 0) > 0;
  const canEdit = canEditEmployment(employment?.employerId) && !hasHistory;

  const defaultValues = useMemo(() => {
    const value = employment.details?.periodStartDate;
    return {
      periodStartDate: value ? new Date(value) : null,
    };
  }, [employment.details]);

  const methods = useForm({ defaultValues, mode: "onChange" });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    methods.reset(defaultValues);
  }, [methods, defaultValues]);

  const validatePeriodStartDate = (date: Date) => {
    return (
      isValid(date) && !isAfter(date, parseISO(employment.details.agreedStart))
    );
  };

  const savePeriodStartDate = (data: FieldValues) => {
    if (!data.periodStartDate) return;
    const newData = {
      employmentId: employment.employmentId,
      version: employment.details.version,
      periodStart: format(data.periodStartDate, isoFormat),
    };

    dispatch(changePeriodStartDate(newData));
  };

  const onSave = () => {
    handleSubmit(savePeriodStartDate)();
    reset();
  };

  return (
    <FormProvider {...methods}>
      <SingleEditFormRow
        name="periodStartDate"
        label={t("talentProfile:periodStarts")}
        helperText={hasHistory ? t("periodStartHistoryReadOnly") : undefined}
        rules={{ required: true, validate: validatePeriodStartDate }}
        canEdit={canEdit}
        onSave={onSave}
        type="Date"
      />
    </FormProvider>
  );
};
